import { useEffect, useRef } from 'react';
import { FormInstance } from 'antd';
import { useQuery } from 'react-query';
import { getNationalAddress } from '~/api/review';
import { useGetDraft, useSaveDraft } from '~/controllers/draft';
import { KsaCompanyInformation } from '~/types/ksa/KsaCompanyInformation';
import {
  hasUsableNationalAddress, NationalAddressPayload, nationalAddressReviewRequired,
} from '~/utils/nationalAddress';

type CompanyDraft = { companyInformation?: KsaCompanyInformation };

export default function useMerchantNationalAddress(
  form: FormInstance,
  applicationId: string | undefined,
  payload: NationalAddressPayload,
) {
  const draft = useGetDraft<CompanyDraft>();
  const saveDraft = useSaveDraft();
  const persistedFor = useRef<string>();
  const ready = !!applicationId && (!draft.isLoading || !!draft.data) && !draft.isError;
  const savedFailure = nationalAddressReviewRequired(payload) ||
    nationalAddressReviewRequired(draft.data) ||
    payload.nationalAddressLookup?.status === 'UNAVAILABLE';
  const savedSuccess = hasUsableNationalAddress(payload.splAddress) ||
    hasUsableNationalAddress(payload.companyInformation?.splAddress);
  const lookup = useQuery(
    ['merchantNationalAddress', applicationId],
    async () => {
      try {
        return hasUsableNationalAddress(await getNationalAddress());
      } catch {
        return false;
      }
    },
    {
      enabled: ready && !savedFailure && !savedSuccess,
      retry: false,
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  );
  const failed = ready && (savedFailure || (!savedSuccess && lookup.data === false));
  const pending = !ready || (!savedFailure && !savedSuccess && lookup.data === undefined);

  useEffect(() => {
    if (!ready || pending) return;
    if (form.getFieldValue(['companyInformation', 'nationalAddressReviewRequired']) !== failed) {
      form.setFieldsValue({ companyInformation: { nationalAddressReviewRequired: failed } });
    }
    // setFieldsValue does not trigger WizardForm autosave. Persist a transport failure
    // immediately too, so reloading does not silently retry or lose the review flag.
    if (failed && !savedFailure && applicationId !== persistedFor.current) {
      persistedFor.current = applicationId;
      const values = form.getFieldsValue(true) as CompanyDraft;
      saveDraft.mutate({
        stepData: {
          companyInformation: {
            ...payload.companyInformation,
            ...draft.data?.companyInformation,
            ...values.companyInformation,
            nationalAddressReviewRequired: true,
          },
        },
      });
    }
  }, [ready, pending, failed, savedFailure, applicationId, form, payload, draft.data, saveDraft]);

  return { failed, pending };
}
